import { Query } from '@softline/core';
import { getVisibleColumns } from '../../grid/query/visible-columns.function';
import { DocumentQueryRow } from '../../types/document-query';
import { DocumentType } from '../../types/document-type';
import {
  getDocumentQueryTypePrefix,
  getDocumentQueryValuesPrefix,
} from './document-query-prefix.function';

/** Belegarten whose query rows carry `{alias}.template` (legacy kzmuster). The
    invoice variants, Kassabon and the purchase types have no template flag, so
    no "Neu aus Vorlage" is offered for them. */
export const TEMPLATE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'OFFER',
  'SALES_ORDER',
  'WORK_ORDER',
  'BLANKET_ORDER',
  'COMMISSION_NOTE',
  'DELIVERY_NOTE',
  'MATERIAL_DELIVERY_NOTE',
  'CONSOLIDATED_MEASUREMENT_REPORT',
  'ROUTING_SLIP',
]);

/** Raw DB value of a Musterbeleg — like the status codes it is not translated
    server-side. */
export const TEMPLATE_DOCUMENT_FLAG = 'J';

export const TEMPLATE_DOCUMENT_PAGE_SIZE = 50;

// Mirrors the guards in define-base-columns: these types have no Belegdatum /
// no values record, so the query must not ask for them.
const NO_DATE_TYPES: ReadonlySet<string> = new Set([
  'WORK_ORDER',
  'CONSOLIDATED_MEASUREMENT_REPORT',
]);
const NO_VALUE_TYPES: ReadonlySet<string> = new Set(['WORK_ORDER']);

export interface TemplateDocument {
  /** Prefixed API id — goes out unchanged as the clone request's `sourceId`. */
  id: string;
  documentType: string;
  documentNumber: number | null;
  /** As delivered by the query service (epoch millis or ISO string). */
  documentDate: number | string | null;
  customerNumber: number | null;
  customerName: string | null;
  constructionSite: string | null;
  project: string | null;
  value: number | null;
  salesOrganisation: string | null;
}

export interface TemplateDocumentPage {
  items: TemplateDocument[];
  hasMore: boolean;
}

export function supportsTemplateDocuments(
  documentType: string | null | undefined
): boolean {
  return !!documentType && TEMPLATE_DOCUMENT_TYPES.has(documentType);
}

export function templateDocumentTypes<T extends Pick<DocumentType, 'type'>>(
  documentTypes: readonly T[]
): T[] {
  return documentTypes.filter((type) => supportsTemplateDocuments(type.type));
}

export function templateDocumentField(documentType: string): string {
  return `${getDocumentQueryTypePrefix(documentType)}.template`;
}

/** Column names the template query selects for a type — only those the type's
    grid exposes, so the request never names a field the backend lacks. */
export function templateDocumentColumns(documentType: string): string[] {
  const prefix = getDocumentQueryTypePrefix(documentType);
  const valuesPrefix = getDocumentQueryValuesPrefix(documentType);
  const visible = getVisibleColumns(documentType);
  const candidates = [
    `${prefix}.document_number`,
    NO_DATE_TYPES.has(documentType) ? null : `${prefix}.document_date`,
    'customer.number',
    `${prefix}.customer_name`,
    'invoice_customer.number',
    'invoice_customer.name',
    `${prefix}.construction_site`,
    'project.project',
    NO_VALUE_TYPES.has(documentType) ? null : `${valuesPrefix}.value`,
    'sales_organisation.sales_organisation',
  ];
  return [
    `${prefix}.id`,
    ...candidates.filter((name): name is string => !!name && visible.has(name)),
  ];
}

/** `POST /document/query/{type}` body for one page of Musterbelege, newest
    Belegnummer first. */
export function templateDocumentQuery(
  documentType: string,
  page = 0
): Query<DocumentQueryRow> {
  const columns = templateDocumentColumns(documentType);
  const numberColumn = `${getDocumentQueryTypePrefix(documentType)}.document_number`;
  return {
    columns: columns.map((name) => ({
      name,
      usage: 'group',
    })) as unknown as Query<DocumentQueryRow>['columns'],
    sorts: (columns.includes(numberColumn)
      ? [{ name: numberColumn, direction: 'DESC' }]
      : []) as unknown as Query<DocumentQueryRow>['sorts'],
    filters: {
      type: 'stringEquals',
      name: templateDocumentField(documentType),
      value: TEMPLATE_DOCUMENT_FLAG,
    } as unknown as Query<DocumentQueryRow>['filters'],
    startRow: page * TEMPLATE_DOCUMENT_PAGE_SIZE,
    endRow: (page + 1) * TEMPLATE_DOCUMENT_PAGE_SIZE,
    version: 1,
  };
}

type RowRecord = Record<string, Record<string, unknown> | undefined>;

function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asString(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}

/** Flattens a query row; rows without the prefixed id are dropped because they
    cannot be cloned. The Lieferkunde stands in for types without a plain
    customer record (e.g. OFFER). */
export function toTemplateDocument(
  row: DocumentQueryRow | null | undefined,
  documentType: string
): TemplateDocument | null {
  if (!row) return null;
  const prefix = getDocumentQueryTypePrefix(documentType);
  const valuesPrefix = getDocumentQueryValuesPrefix(documentType);
  const record = row as unknown as RowRecord;
  const document = record[prefix];
  const id = asString(document?.['id']);
  if (!id) return null;
  const customer = record['customer'];
  const invoiceCustomer = record['invoice_customer'];
  const date = document?.['document_date'];
  return {
    id,
    documentType,
    documentNumber: asNumber(document?.['document_number']),
    documentDate: typeof date === 'number' || typeof date === 'string' ? date : null,
    customerNumber: asNumber(customer?.['number'] ?? invoiceCustomer?.['number']),
    customerName: asString(document?.['customer_name'] ?? invoiceCustomer?.['name']),
    constructionSite: asString(document?.['construction_site']),
    project: asString(record['project']?.['project']),
    value: asNumber(record[valuesPrefix]?.['value']),
    salesOrganisation: asString(record['sales_organisation']?.['sales_organisation']),
  };
}

/** Client-side narrowing of the loaded page by Belegnummer, Kunde, Baustelle or
    Projekt. */
export function filterTemplateDocuments(
  items: readonly TemplateDocument[],
  term: string | null | undefined
): TemplateDocument[] {
  const needle = term?.trim().toLowerCase() ?? '';
  if (!needle) return [...items];
  return items.filter((item) =>
    [
      item.documentNumber,
      item.customerNumber,
      item.customerName,
      item.constructionSite,
      item.project,
    ].some((value) => value != null && String(value).toLowerCase().includes(needle))
  );
}

/** `lastRow` is AG's SSRM total (negative = unknown); without it a full page
    means there may be another one. */
export function hasMoreTemplateDocuments(
  result: { data?: unknown[] | null; lastRow?: number | null } | null | undefined,
  page: number
): boolean {
  const endRow = (page + 1) * TEMPLATE_DOCUMENT_PAGE_SIZE;
  const total = result?.lastRow;
  if (typeof total === 'number' && total >= 0) return total > endRow;
  return (result?.data?.length ?? 0) >= TEMPLATE_DOCUMENT_PAGE_SIZE;
}
